import classNames from 'classnames';
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useLayout, useSite } from '../../contexts';

import { Block, HomePage } from '../../types';
import Blocks from '../blocks/Blocks';

import styles from '../../../styles/partials/home-splash.module.css';

const Animation = lazy(() => import('./Animation'));

interface HomeSplashProps {
    page?: HomePage;
    blocks?: Block[] | null;
    className?: string | null;
}

function HomeSplash({ page = null, blocks = null, className = null }: HomeSplashProps) {
    const { size } = useLayout();
    const { animationComplete, setAnimationComplete } = useSite();

    const logoRef = useRef(null);
    const mapRef = useRef(null);
    const startingRef = useRef(false);

    const [isStopped, setIsStopped] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const [logoReady, setLogoReady] = useState(false);
    const [mapReady, setMapReady] = useState(false);

    const [logoComplete, setLogoComplete] = useState(animationComplete);
    const [mapComplete, setMapComplete] = useState(animationComplete);
    const [blocksVisible, setBlocksVisible] = useState(true);

    const isSmall = useMemo(() => size.width < 760, [size.width]);

    const gotoAnimationEnd = useCallback(() => {
        if (logoRef.current) {
            logoRef.current.goToAndStop(logoRef.current.getDuration(true), true);
        }
        if (mapRef.current) {
            mapRef.current.goToAndStop(mapRef.current.getDuration(true), true);
        }
    }, []);

    const onLogoReady = useCallback(() => {
        setLogoReady(true);
    }, [setLogoReady]);

    const onMapReady = useCallback(() => {
        setMapReady(true);
    }, [setMapReady]);

    const onLogoComplete = useCallback(() => {
        setLogoComplete(true);
    }, [setLogoComplete]);

    const onMapComplete = useCallback(() => {
        setMapComplete(true);
    }, [setMapComplete]);

    // const onMapEnterFrame = useCallback(
    //     (e) => {
    //         const progress = e.currentTime / e.totalTime;
    //         if (progress > 0.6) {
    //             setBlocksVisible(true);
    //         }
    //     },
    //     [setBlocksVisible],
    // );

    useEffect(() => {
        if (mapComplete && logoComplete) {
            setAnimationComplete(true);
        }
    }, [mapComplete, logoComplete, animationComplete, setAnimationComplete]);

    useEffect(() => {
        if (startingRef.current === false && isPaused === true && logoReady && mapReady) {
            startingRef.current = true;
            // setTimeout(() => {
            //     console.log('shift me');
            //     // setIsPaused(false);
            // }, 1500);
        }
    }, [isPaused, logoReady, mapReady, animationComplete, setIsPaused]);

    useEffect(() => {
        setTimeout(() => {
            setIsStopped(false);
        }, 1000);
    }, [setIsStopped]);

    useEffect(() => {
        if (animationComplete) {
            gotoAnimationEnd();
        }
    }, [isSmall, animationComplete, gotoAnimationEnd]);

    const logoEventListeners = [
        {
            eventName: 'DOMLoaded',
            callback: onLogoReady,
        },
        {
            eventName: 'complete',
            callback: onLogoComplete,
        },
    ];

    const mapEventListeners = [
        {
            eventName: 'DOMLoaded',
            callback: onMapReady,
        },
        // {
        //     eventName: 'enterFrame',
        //     callback: onMapEnterFrame,
        // },
        {
            eventName: 'complete',
            callback: onMapComplete,
        },
    ];

    // console.log(isStopped);

    return (
        <div
            className={classNames({
                [styles.container]: true,
                [className]: className !== null,
            })}
        >
            <div className={classNames(['page-inner', styles.titleContainer])}>
                <h1 className={styles.title}>
                    <FormattedMessage defaultMessage="Signé Laval" description="Site name" />
                </h1>
                <div
                    className={classNames([
                        styles.animation,
                        {
                            [styles.isSmall]: isSmall,
                            [styles.isComplete]: animationComplete,
                        },
                    ])}
                >
                    <div role="img" aria-label="Signé Laval">
                        <Suspense fallback={null}>
                            <Animation
                                name="logo"
                                speed={2}
                                isPaused={isPaused}
                                eventListeners={logoEventListeners}
                            />
                        </Suspense>
                    </div>
                </div>
            </div>
            {blocks !== null && blocks.length > 0 ? (
                <div
                    className={classNames([
                        styles.blocksContainer,
                        {
                            [styles.visible]: blocksVisible,
                        },
                    ])}
                >
                    <div className={styles.pageBlocks}>
                        <Blocks page={page} blocks={blocks} isHomePage />
                    </div>
                </div>
            ) : null}
            <div className={classNames(['fullscreen', styles.background])}>
                <div role="img" aria-hidden="true">
                    <Suspense fallback={null}>
                        <Animation
                            name="map"
                            speed={2}
                            isPaused={isStopped}
                            isStopped={isStopped}
                            eventListeners={mapEventListeners}
                        />
                    </Suspense>
                </div>
            </div>
        </div>
    );
}

export default HomeSplash;
